interface XLabel { i: number; t: string }

export function BarChart({ data, xlabels, height = 190, fmtY, color = 'var(--accent)' }: {
  data: number[];
  xlabels?: XLabel[];
  height?: number;
  fmtY?: (v: number) => string;
  color?: string;
}) {
  const W = 660, H = height;
  const padL = 34, padR = 8, padB = 20, padT = 8;
  const iw = W - padL - padR, ih = H - padT - padB;
  const max = Math.max(1, ...data) * 1.12;
  const n = data.length;
  const bw = iw / n;
  const bar = Math.min(bw * 0.62, 16);

  const ticks = Array.from({ length: 5 }, (_, i) => {
    const v = max * i / 4;
    const y = padT + ih - (v / max) * ih;
    return (
      <g key={i}>
        <line className="gridline" x1={padL} x2={W - padR} y1={y} y2={y} />
        <text className="axis-y" x={padL - 7} y={y + 3} textAnchor="end">{fmtY ? fmtY(v) : Math.round(v)}</text>
      </g>
    );
  });

  const bars = data.map((v, i) => {
    const bh = (v / max) * ih;
    const x = padL + i * bw + (bw - bar) / 2;
    const y = padT + ih - bh;
    return (
      <rect key={i} className="bar" x={x} y={y} width={bar} height={Math.max(1, bh)} rx={3}
        fill={color} opacity={i === n - 1 ? 1 : 0.42} />
    );
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none"
      style={{ display: 'block', overflow: 'visible' }}>
      {ticks}
      {bars}
      {(xlabels ?? []).map((l, i) => (
        <text key={i} className="axis-x" x={padL + (l.i + 0.5) * bw} y={H - 6} textAnchor="middle">{l.t}</text>
      ))}
    </svg>
  );
}

export function Sparkline({ data, width = 120, height = 34, id = '0', color = 'var(--accent)' }: {
  data: number[];
  width?: number;
  height?: number;
  id?: string;
  color?: string;
}) {
  const max = Math.max(1, ...data), min = Math.min(...data);
  const n = data.length, span = max - min || 1;
  const pts = data.map((v, i) => [i / (n - 1) * width, height - ((v - min) / span) * (height - 4) - 2] as const);
  const d = pts.map((pt, i) => (i ? 'L' : 'M') + pt[0].toFixed(1) + ' ' + pt[1].toFixed(1)).join(' ');
  const area = d + ` L ${width} ${height} L 0 ${height} Z`;
  const gId = 'sg' + id;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gId} x1={0} y1={0} x2={0} y2={1}>
          <stop offset="0%" stopColor={color} stopOpacity={0.24} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Donut({ slices, centerV, centerL }: {
  slices: { label: string; value: number; color: string; display: string; sub?: string }[];
  centerV: string;
  centerL?: string;
}) {
  const total = slices.reduce((a, s) => a + s.value, 0) || 1;
  const R = 54, sw = 20, C = 2 * Math.PI * R, cx = 66, cy = 66;
  let off = 0;

  const arcs = slices.map((s, i) => {
    const frac = s.value / total, len = frac * C;
    const el = (
      <circle key={i} cx={cx} cy={cy} r={R} fill="none" stroke={s.color} strokeWidth={sw}
        strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-off}
        transform={`rotate(-90 ${cx} ${cy})`} />
    );
    off += len;
    return el;
  });

  return (
    <div className="donut-wrap">
      <svg width={132} height={132} viewBox="0 0 132 132">
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--track)" strokeWidth={sw} />
        {arcs}
        <text x={cx} y={cy - 3} textAnchor="middle" fontFamily="var(--display)" fontSize={22} fontWeight={600} fill="var(--ink)">{centerV}</text>
        <text x={cx} y={cy + 15} textAnchor="middle" fontFamily="var(--label-font)" fontSize={9} fill="var(--faint)" letterSpacing=".06em">{(centerL ?? '').toUpperCase()}</text>
      </svg>
      <div className="donut-legend">
        {slices.map((s, i) => (
          <div className="dl" key={i}>
            <span className="sw" style={{ background: s.color }} />
            <div>
              <div className="dt">{s.label} <b>{s.display}</b></div>
              {s.sub && <div className="dv">{s.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
